import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Plus, Eye, Sparkles, MoreHorizontal, Globe, Lock, Image, Share2, Trash2, Settings, Loader2, Star, Pencil, Check, X } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import ConfirmDialog from '@/components/ConfirmDialog';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get full image URL
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/static/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

const BookList = ({ books = [], seriesId, activeJobs = [], onCreateBook, onBookUpdate, onBookDelete, onJobCancel, isSeriesCompleted = false }) => {
  const navigate = useNavigate();
  const [processingBookId, setProcessingBookId] = useState(null);
  const [processingAction, setProcessingAction] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [bookToDelete, setBookToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [cancellingJobId, setCancellingJobId] = useState(null);

  // Rename state
  const [editingBookId, setEditingBookId] = useState(null);
  const [editedTitle, setEditedTitle] = useState('');
  const [savingTitle, setSavingTitle] = useState(false);

  // Handle job cancellation
  const handleCancelJob = async (jobId, e) => {
    e.stopPropagation();
    setCancellingJobId(jobId);
    try {
      await onJobCancel?.(jobId);
    } finally {
      setCancellingJobId(null);
    }
  };

  // Toggle visibility
  const handleToggleVisibility = async (book, e) => {
    e.stopPropagation();
    const storyId = book.story_id || book.id;
    const newVisibility = !(book.is_public ?? true);

    setProcessingBookId(storyId);
    setProcessingAction('visibility');

    try {
      await axios.patch(`${API}/stories/${storyId}`, {
        is_public: newVisibility
      }, { withCredentials: true });

      toast.success(newVisibility ? 'Book is now public' : 'Book is now private');
      onBookUpdate?.({ ...book, is_public: newVisibility });
    } catch (error) {
      console.error('Failed to update visibility:', error);
      toast.error('Failed to update visibility');
    } finally {
      setProcessingBookId(null);
      setProcessingAction(null);
    }
  };

  // Regenerate thumbnail
  const handleRegenerateThumbnail = async (book, e) => {
    e.stopPropagation();
    const storyId = book.story_id || book.id;

    setProcessingBookId(storyId);
    setProcessingAction('thumbnail');
    toast.info("Regenerating thumbnail...");

    try {
      const response = await axios.post(
        `${API}/stories/${storyId}/regenerate-thumbnail`,
        {},
        { withCredentials: true }
      );

      toast.success("Thumbnail regenerated!");
      onBookUpdate?.({ ...book, thumbnail: response.data.thumbnail });
    } catch (error) {
      console.error("Failed to regenerate thumbnail:", error);
      toast.error(error.response?.data?.detail || "Failed to regenerate thumbnail");
    } finally {
      setProcessingBookId(null);
      setProcessingAction(null);
    }
  };

  // Share book
  const handleShare = async (book, e) => {
    e.stopPropagation();
    const storyId = book.story_id || book.id;
    const shareUrl = `${window.location.origin}/story/${storyId}`;

    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard!');
    } catch (error) {
      // Fallback for browsers that don't support clipboard API
      const textarea = document.createElement('textarea');
      textarea.value = shareUrl;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      toast.success('Link copied to clipboard!');
    }
  };

  // Start editing book title
  const startEditingTitle = (book, e) => {
    e.stopPropagation();
    const storyId = book.story_id || book.id;
    setEditingBookId(storyId);
    setEditedTitle(book.title || '');
  };

  // Cancel editing
  const cancelEditingTitle = (e) => {
    if (e) e.stopPropagation();
    setEditingBookId(null);
    setEditedTitle('');
  };

  // Save renamed title
  const handleSaveTitle = async (book, e) => {
    if (e) e.stopPropagation();
    if (savingTitle) return;

    const storyId = book.story_id || book.id;
    const trimmedTitle = editedTitle.trim();

    if (!trimmedTitle) {
      toast.error("Title cannot be empty");
      return;
    }

    if (trimmedTitle === book.title) {
      cancelEditingTitle();
      return;
    }

    setSavingTitle(true);
    try {
      await axios.patch(`${API}/stories/${storyId}`, {
        title: trimmedTitle
      }, { withCredentials: true });

      toast.success('Title updated!');
      onBookUpdate?.({ ...book, title: trimmedTitle });
      setEditingBookId(null);
      setEditedTitle('');
    } catch (error) {
      console.error('Failed to update title:', error);
      toast.error(error.response?.data?.detail || 'Failed to update title');
    } finally {
      setSavingTitle(false);
    }
  };

  // Open delete dialog
  const openDeleteDialog = (book, e) => {
    e.stopPropagation();
    setBookToDelete(book);
    setShowDeleteDialog(true);
  };

  // Delete book
  const handleDeleteBook = async () => {
    if (!bookToDelete) return;

    const storyId = bookToDelete.story_id || bookToDelete.id;
    setDeleting(true);

    try {
      await axios.delete(`${API}/stories/${storyId}`, { withCredentials: true });
      toast.success('Book deleted successfully');
      onBookDelete?.(storyId);
      setShowDeleteDialog(false);
      setBookToDelete(null);
    } catch (error) {
      console.error('Failed to delete book:', error);
      toast.error('Failed to delete book');
    } finally {
      setDeleting(false);
    }
  };

  // Show empty state only if no books AND no active jobs
  if (books.length === 0 && activeJobs.length === 0) {
    return (
      <div className="text-center py-12">
        <div
          className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
          style={{ background: 'var(--st-bg-surface)' }}
        >
          <BookOpen className="w-8 h-8" style={{ color: '#FDBA74' }} />
        </div>
        <p className="font-semibold mb-1" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}>
          No books yet
        </p>
        <p className="text-sm mb-5" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
          {isSeriesCompleted ? 'This series is completed.' : 'Start your series with the first book!'}
        </p>
        {!isSeriesCompleted && (
          <button
            onClick={onCreateBook}
            className="px-5 py-2.5 rounded-xl font-semibold text-sm transition-all hover:scale-[1.02] text-white"
            style={{
              background: 'linear-gradient(135deg, #818CF8 0%, #6366F1 100%)',
              fontFamily: 'Quicksand, sans-serif',
              boxShadow: '0 4px 14px rgba(99, 102, 241, 0.3)'
            }}
          >
            <Plus className="w-4 h-4 inline-block mr-1.5" />
            Create Book 1
          </button>
        )}
      </div>
    );
  }

  // Sort books by book_number
  const sortedBooks = [...books].sort((a, b) => (a.book_number || 0) - (b.book_number || 0));

  return (
    <>
      <div className="space-y-3">
        {/* Existing Books (sorted by book_number) */}
        {sortedBooks.map((book, index) => {
          // Handle both story_id (from book summaries) and id (from stories collection)
          const storyId = book.story_id || book.id;
          // Handle both thumbnail_url and thumbnail field names
          const thumbnailUrl = book.thumbnail_url || book.thumbnail;
          const isPublic = book.is_public ?? true;
          const isProcessing = processingBookId === storyId;
          // Handle both version (from stories) and story_version field names
          const storyVersion = book.version || book.story_version;

          const bookNumber = book.book_number || index + 1;
          const formattedNumber = String(bookNumber).padStart(2, '0');

          return (
            <div
              key={storyId || index}
              className="flex items-center gap-4 p-3 rounded-2xl hover:shadow-sm transition-all group"
              style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}
            >
              {/* Book Number - Large text style like home widget */}
              <div
                className="w-10 flex-shrink-0 text-center cursor-pointer"
                style={{
                  fontFamily: 'Chewy, cursive',
                  fontSize: '1.5rem',
                  color: '#D4B896',
                  letterSpacing: '-0.02em'
                }}
                onClick={() => navigate(`/story/${storyId}`)}
              >
                {formattedNumber}
              </div>

              {/* Thumbnail */}
              <div
                className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 cursor-pointer"
                style={{ background: 'linear-gradient(145deg, var(--st-border-light) 0%, var(--st-border-medium) 100%)' }}
                onClick={() => navigate(`/story/${storyId}`)}
              >
                {thumbnailUrl ? (
                  <img
                    src={getImageUrl(thumbnailUrl)}
                    alt={book.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Sparkles className="w-5 h-5" style={{ color: '#FFCDB2' }} />
                  </div>
                )}
              </div>

              {/* Content */}
              <div
                className="flex-1 min-w-0"
                onClick={() => editingBookId !== storyId && navigate(`/story/${storyId}`)}
                style={{ cursor: editingBookId === storyId ? 'default' : 'pointer' }}
              >
                {editingBookId === storyId ? (
                  // Inline editing mode
                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="text"
                      value={editedTitle}
                      onChange={(e) => setEditedTitle(e.target.value)}
                      className="flex-1 text-sm font-bold px-2 py-1 rounded-lg border-2 focus:outline-none focus:border-orange-400 min-w-0"
                      style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)', borderColor: 'var(--st-border-light)' }}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveTitle(book, e);
                        if (e.key === 'Escape') cancelEditingTitle(e);
                      }}
                      disabled={savingTitle}
                    />
                    <button
                      onClick={(e) => handleSaveTitle(book, e)}
                      disabled={savingTitle}
                      className="p-1 rounded-full hover:bg-green-50 dark:hover:bg-white/10 flex-shrink-0"
                    >
                      {savingTitle ? (
                        <Loader2 className="w-4 h-4 animate-spin text-green-600" />
                      ) : (
                        <Check className="w-4 h-4 text-green-600" />
                      )}
                    </button>
                    <button
                      onClick={cancelEditingTitle}
                      disabled={savingTitle}
                      className="p-1 rounded-full hover:bg-red-50 dark:hover:bg-white/10 flex-shrink-0"
                    >
                      <X className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                ) : (
                  // Normal display mode
                  <>
                    <div className="flex items-center gap-2">
                      <h4
                        className="font-bold text-sm"
                        style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}
                      >
                        {book.title || `Book ${book.book_number || index + 1}`}
                      </h4>
                      {/* Version Badge */}
                      {storyVersion && (
                        <span
                          className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-semibold"
                          style={{
                            background: storyVersion === 3 ? '#F3E8FF' : storyVersion === 2 ? '#EFF6FF' : '#F5F5F5',
                            color: storyVersion === 3 ? '#7C3AED' : storyVersion === 2 ? '#3B82F6' : '#6B7280'
                          }}
                        >
                          {storyVersion === 3 && <Star className="w-2.5 h-2.5" />}
                          v{storyVersion}
                        </span>
                      )}
                      {!isPublic && (
                        <Lock className="w-3 h-3 flex-shrink-0" style={{ color: 'var(--st-text-muted)' }} />
                      )}
                    </div>

                    {/* Key Events */}
                    {book.key_events?.length > 0 && (
                      <p
                        className="text-xs line-clamp-1 mt-0.5"
                        style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
                      >
                        {book.key_events.join(' • ')}
                      </p>
                    )}

                    {/* Unresolved Threads */}
                    {book.unresolved_threads?.length > 0 && (
                      <div className="flex items-center gap-1 mt-1.5">
                        <span
                          className="text-xs px-2 py-0.5 rounded-full font-medium"
                          style={{ background: '#FEF3C7', color: '#D97706' }}
                        >
                          {book.unresolved_threads.length} open thread{book.unresolved_threads.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 flex-shrink-0">
                {/* Loading indicator for processing */}
                {isProcessing && (
                  <div className="p-2">
                    <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--st-primary)' }} />
                  </div>
                )}

                {/* More Options Menu */}
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="p-2 rounded-xl opacity-100 sm:opacity-0 sm:group-hover:opacity-100 hover:bg-black/5 dark:hover:bg-white/10 transition-all focus:opacity-100"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                      onPointerDown={(e) => e.stopPropagation()}
                    >
                      <MoreHorizontal className="w-4 h-4" style={{ color: 'var(--st-text-muted)' }} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48" sideOffset={5}>
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/story/${storyId}`);
                      }}
                      className="cursor-pointer"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      View Story
                    </DropdownMenuItem>

                    <DropdownMenuItem
                      onClick={(e) => startEditingTitle(book, e)}
                      className="cursor-pointer"
                    >
                      <Pencil className="w-4 h-4 mr-2" />
                      Rename
                    </DropdownMenuItem>

                    <DropdownMenuItem
                      onClick={(e) => handleToggleVisibility(book, e)}
                      className="cursor-pointer"
                      disabled={isProcessing}
                    >
                      {isPublic ? (
                        <>
                          <Lock className="w-4 h-4 mr-2" />
                          Make Private
                        </>
                      ) : (
                        <>
                          <Globe className="w-4 h-4 mr-2" />
                          Make Public
                        </>
                      )}
                    </DropdownMenuItem>

                    <DropdownMenuItem
                      onClick={(e) => handleRegenerateThumbnail(book, e)}
                      className="cursor-pointer"
                      disabled={isProcessing}
                    >
                      <Image className="w-4 h-4 mr-2" />
                      Regenerate Thumbnail
                    </DropdownMenuItem>

                    <DropdownMenuItem
                      onClick={(e) => handleShare(book, e)}
                      className="cursor-pointer"
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      Copy Link
                    </DropdownMenuItem>

                    <DropdownMenuSeparator />

                    <DropdownMenuItem
                      onClick={(e) => openDeleteDialog(book, e)}
                      className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Book
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          );
        })}

        {/* Generating Books (Active Jobs) - shown after existing books, in order */}
        {activeJobs.map((job, jobIndex) => {
          // Only count completed books (not generating) for next book number
          const completedBooksCount = (books || []).filter(
            b => b.generation_status?.status !== 'generating'
          ).length;
          const nextBookNumber = completedBooksCount + jobIndex + 1;
          const formattedNumber = String(nextBookNumber).padStart(2, '0');

          return (
            <div
              key={job.id}
              className="flex items-center gap-4 p-3 rounded-2xl transition-all animate-pulse cursor-pointer hover:shadow-md"
              style={{ background: 'linear-gradient(135deg, var(--st-bg-page) 0%, var(--st-border-light) 100%)', border: '2px solid var(--st-border-medium)' }}
              onClick={() => navigate(`/generating/${job.id}`)}
            >
              {/* Book Number */}
              <div
                className="w-10 flex-shrink-0 text-center"
                style={{
                  fontFamily: 'Chewy, cursive',
                  fontSize: '1.5rem',
                  color: 'var(--st-primary)',
                  letterSpacing: '-0.02em'
                }}
              >
                {formattedNumber}
              </div>

              {/* Thumbnail Placeholder with Spinner */}
              <div
                className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 flex items-center justify-center"
                style={{ background: 'linear-gradient(145deg, var(--st-border-light) 0%, var(--st-border-medium) 100%)' }}
              >
                {job.thumbnail_url ? (
                  <img
                    src={getImageUrl(job.thumbnail_url)}
                    alt="Preview"
                    className="w-full h-full object-cover opacity-70"
                  />
                ) : (
                  <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--st-primary)' }} />
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h4
                    className="font-bold text-sm"
                    style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-heading)' }}
                  >
                    Creating Book {nextBookNumber}...
                  </h4>
                  <Sparkles className="w-3.5 h-3.5" style={{ color: 'var(--st-primary)' }} />
                  {/* Version Badge for in-progress */}
                  {job.story_version && (
                    <span
                      className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-semibold"
                      style={{
                        background: job.story_version === 3 ? '#F3E8FF' : job.story_version === 2 ? '#EFF6FF' : '#F5F5F5',
                        color: job.story_version === 3 ? '#7C3AED' : job.story_version === 2 ? '#3B82F6' : '#6B7280'
                      }}
                    >
                      {job.story_version === 3 && <Star className="w-2.5 h-2.5" />}
                      v{job.story_version}
                    </span>
                  )}
                </div>

                {/* Progress */}
                <div className="mt-1.5">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs" style={{ color: 'var(--st-text-muted)' }}>
                      {job.status === 'queued' ? `Queue position: #${job.queue_position || 1}` : (job.message || 'Processing...')}
                    </span>
                    <span className="text-xs font-semibold" style={{ color: 'var(--st-primary)' }}>
                      {job.status === 'queued' ? 'Waiting' : `${job.progress || 0}%`}
                    </span>
                  </div>
                  {job.status === 'processing' && (
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--st-border-light)' }}>
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${job.progress || 0}%`,
                          background: 'linear-gradient(90deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)'
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Cancel Button */}
              {job.status === 'queued' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCancelJob(job.id, e);
                  }}
                  disabled={cancellingJobId === job.id}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors hover:bg-red-100"
                  style={{ color: '#EF4444', background: '#FEE2E2' }}
                >
                  {cancellingJobId === job.id ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    'Cancel'
                  )}
                </button>
              )}
            </div>
          );
        })}

        {/* Create Next Book Button - only show if series is not completed */}
        {!isSeriesCompleted && (
          <button
            className="w-full flex items-center gap-4 p-4 rounded-2xl transition-all hover:shadow-sm cursor-pointer"
            style={{ background: 'var(--st-bg-surface)', border: '2px dashed #818CF8' }}
            onClick={onCreateBook}
          >
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'rgba(99, 102, 241, 0.1)' }}
            >
              <Plus className="w-5 h-5" style={{ color: '#6366F1' }} />
            </div>
            <div className="text-left">
              <p
                className="font-bold text-sm"
                style={{ fontFamily: 'Quicksand, sans-serif', color: '#6366F1' }}
              >
                Create Book {(books || []).filter(b => b.generation_status?.status !== 'generating').length + activeJobs.length + 1}
              </p>
              <p className="text-xs" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                Continue the adventure
              </p>
            </div>
          </button>
        )}
      </div>

      {/* Delete Book Confirmation Dialog */}
      <ConfirmDialog
        open={showDeleteDialog}
        onOpenChange={(open) => {
          setShowDeleteDialog(open);
          if (!open) setBookToDelete(null);
        }}
        title="Delete Book?"
        description={`This will permanently delete "${bookToDelete?.title || 'this book'}" from your series. This action cannot be undone.`}
        confirmText="Delete Book"
        onConfirm={handleDeleteBook}
        loading={deleting}
        variant="danger"
      />
    </>
  );
};

export default BookList;
